import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Building2,
  Briefcase,
  MapPin,
  Calendar,
  Filter,
  User,
  Sparkles,
  ExternalLink,
  Target,
  RefreshCw,
} from 'lucide-react';
import { fetchPortalAttendees, PortalAttendee } from '../../lib/api';

interface PortalAttendeeDirectoryProps {
  onScheduleMeeting: (attendee: PortalAttendee) => void;
  currentUserId?: string;
}

const INDUSTRY_SEGMENTS = [
  'All Industries',
  'Agrochemicals & Technical Formulations',
  'Biologicals & Bio-Stimulants',
  'Fertilizers & Specialized Plant Nutrition',
  'Seeds & Crop Genetics',
  'Agricultural Drones & Precision Tech',
  'Farm Machinery & Mechanization',
  'Micro-Irrigation & Water Technologies',
  'Supply Chain & Post-Harvest Storage',
  'Agri-Banking, Trade Finance & Export',
];

const BUSINESS_NATURES = [
  'All Business Types',
  'Manufacturer / Primary Producer',
  'National / State Wholesaler & Distributor',
  'Retailer / Dealer Network',
  'Importer / International Trading House',
  'Contract Research (CRO) / Analytical Lab',
  'Farmer Producer Organization (FPO)',
  'Corporate Farming / Plantation Operator',
  'Government, Regulatory or PSU Delegate',
  'Venture Capital / Private Equity Investor',
];

export function PortalAttendeeDirectory({
  onScheduleMeeting,
  currentUserId,
}: PortalAttendeeDirectoryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All Industries');
  const [selectedNature, setSelectedNature] = useState('All Business Types');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['portal-attendees', searchQuery, selectedIndustry, selectedNature],
    queryFn: () =>
      fetchPortalAttendees({
        query: searchQuery.trim() || undefined,
        industrySegment: selectedIndustry !== 'All Industries' ? selectedIndustry : undefined,
        businessNature: selectedNature !== 'All Business Types' ? selectedNature : undefined,
      }),
  });

  const attendees = (data?.attendees || []).filter((a) => a.id !== currentUserId);

  return (
    <div className="space-y-6">
      {/* Search & Filters */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search delegates by name, company, designation, or city..."
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:bg-white"
            />
          </div>

          <div className="flex gap-2">
            <select
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 focus:outline-none focus:border-[#0A3D27]"
            >
              {INDUSTRY_SEGMENTS.map((seg) => (
                <option key={seg} value={seg}>
                  {seg}
                </option>
              ))}
            </select>

            <select
              value={selectedNature}
              onChange={(e) => setSelectedNature(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-xs text-slate-700 focus:outline-none focus:border-[#0A3D27]"
            >
              {BUSINESS_NATURES.map((nat) => (
                <option key={nat} value={nat}>
                  {nat}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Directory Grid */}
      {isLoading ? (
        <div className="text-center py-16">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#0A3D27]" />
          <p className="text-xs text-slate-500 mt-2">Loading delegate directory...</p>
        </div>
      ) : attendees.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-slate-200 p-8 space-y-2">
          <User className="w-10 h-10 text-slate-400 mx-auto" />
          <h3 className="text-sm font-bold text-slate-900">No matching attendees found</h3>
          <p className="text-xs text-slate-500">Try adjusting your search criteria or filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {attendees.map((att) => (
            <div
              key={att.id}
              className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 text-[#0A3D27] flex items-center justify-center font-bold text-base overflow-hidden">
                      {att.avatarUrl ? (
                        <img src={att.avatarUrl} alt={att.fullName} className="w-full h-full object-cover" />
                      ) : (
                        <span>{att.fullName.slice(0, 2).toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 leading-snug">{att.fullName}</h4>
                      <p className="text-xs text-slate-600 font-medium">
                        {att.jobTitle || 'Trade Delegate'}
                        {att.companyName ? ` • ${att.companyName}` : ''}
                      </p>
                      {att.cityAndCountry && (
                        <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          <span>{att.cityAndCountry}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 capitalize">
                    {att.tier.replace('_', ' ')}
                  </span>
                </div>

                {att.bio && (
                  <p className="text-xs text-slate-600 line-clamp-2 italic bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                    "{att.bio}"
                  </p>
                )}

                {/* Matchmaking Tags */}
                <div className="space-y-1.5 pt-1">
                  {att.industrySegment && (
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
                      <Briefcase className="w-3 h-3 text-emerald-700 shrink-0" />
                      <span className="truncate">{att.industrySegment}</span>
                    </div>
                  )}

                  {att.lookingFor && att.lookingFor.length > 0 && (
                    <div className="flex flex-wrap gap-1 items-center pt-1">
                      <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Seeking:</span>
                      {att.lookingFor.map((item, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-100 text-[10px] text-[#0A3D27] font-medium"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Action */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                {att.linkedinUrl ? (
                  <a
                    href={att.linkedinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <span>LinkedIn</span>
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span />
                )}

                <button
                  onClick={() => onScheduleMeeting(att)}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#0A3D27] hover:bg-[#062B1B] text-white text-xs font-bold shadow-xs transition-all cursor-pointer"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Request Meeting</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PortalAttendeeDirectory;
